import React from "react";
import { Link } from "react-router-dom";
import { useUserContext } from "../../../contexts/UserContext";
import { getSideNavItems } from "../../../config/sidenav";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import Card from "../../../components/UI/Card";
import Button from "../../../components/UI/Button";
import { ArrowUpRight, Building2 } from "lucide-react";

const Admin = () => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const filterByPermission = (routes, route_permission) => {
        if (!userData?.selectedOrganization || !route_permission) return [];

        return routes
            ?.map((route) => {
                if (route.id === 'manage-csp' && route_permission['admin']) return route;
                if (route.id === 'workspaces' && (route_permission['workspaces'] || route_permission['admin'] || route_permission['organization'])) return route;
                return route_permission[route.id] ? route : null;
            })
            .filter((feature) => feature !== null);
    };

    const filteredRoutes = filterByPermission(
        getSideNavItems(content?.LEFT_SIDE_NAV_OPTIONS, "admin"),
        userData?.selectedOrganization?.level?.route_permission
    );

    return (
        <div className="stride-page-shell">
            <div className="w-full space-y-6">
                <div>
                    <span className="stride-eyebrow">Administration</span>
                    <h1 className="text-2xl font-bold tracking-tight text-stride-heading mt-0.5">
                        {content?.ADMIN_DASHBOARD_CONTENT?.welcome_message || "Admin Control Center"}
                    </h1>
                    <p className="text-sm text-stride-body mt-1">
                        Configure project settings, organization security policies, and administrative operations.
                    </p>
                </div>

                {userData?.selectedOrganization ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredRoutes?.map((item) => (
                            <Card
                                key={item.id}
                                variant="accent"
                                padding="none"
                                className="group relative flex flex-col justify-between p-5 rounded-xl bg-gradient-to-b from-white via-white to-primary-soft/20 border border-border-subtle hover:border-primary/40 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
                            >
                                <div>
                                    <div className="flex justify-between items-start">
                                        <div className="w-10 h-10 rounded-xl bg-primary-soft border border-primary/20 flex items-center justify-center p-2 text-primary group-hover:scale-105 transition-transform shrink-0 shadow-xs">
                                            {item.icon}
                                        </div>
                                        <Link
                                            to={item.route}
                                            className="p-1.5 rounded-md text-stride-muted hover:text-primary hover:bg-primary-soft transition-colors"
                                            aria-label={`Open ${item.title}`}
                                        >
                                            <ArrowUpRight className="w-4 h-4" />
                                        </Link>
                                    </div>

                                    <h2 className="text-base font-semibold text-stride-heading mt-4 mb-1.5 capitalize line-clamp-1 group-hover:text-primary transition-colors">
                                        {item.title}
                                    </h2>
                                    <p className="text-xs text-stride-muted leading-relaxed line-clamp-2 min-h-[32px]">
                                        {item.description}
                                    </p>
                                </div>

                                <div className="mt-5 pt-3 border-t border-border-subtle/80">
                                    <Link to={item.route} className="w-full block">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            icon={<ArrowUpRight size={14} />}
                                            className="w-full justify-center"
                                        >
                                            {content?.ADMIN_DASHBOARD_CONTENT?.manage_btn || "Manage"}
                                        </Button>
                                    </Link>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="h-12 w-12 rounded-full bg-stride-bg border border-stride-border flex items-center justify-center text-stride-muted mb-3">
                            <Building2 className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-semibold text-stride-heading">
                            {content?.ADMIN_DASHBOARD_CONTENT?.no_organization_selected || "No Organization Selected"}
                        </h3>
                        <p className="text-sm text-stride-muted mt-1 max-w-sm">
                            Please select an active organization from the top switcher to access administrative settings.
                        </p>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default Admin;
